import { useState, useMemo } from 'react';
import {
  globalActivityEntries,
  activityTypeConfigs,
  filterConfigs,
  globalActivityStats,
  activityRules,
  getActivitiesByFilter,
  getActivityDistribution,
  getFilterDistribution,
  mockGlobalActivityTimes,
} from '@/mocks/rfwGlobalActivity';

type SortMode = 'recientes' | 'mas_reacciones' | 'mas_comentarios';
type ViewMode = 'feed' | 'categorias' | 'estadisticas' | 'reglas';

function timeAgo(timestamp: string): string {
  const diff = Date.now() - new Date(timestamp).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Ahora';
  if (mins < 60) return `Hace ${mins} min`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `Hace ${hours}h`;
  const days = Math.floor(hours / 24);
  return `Hace ${days}d`;
}

export default function ActividadGlobalPage() {
  const [view, setView] = useState<ViewMode>('feed');
  const [selectedFilter, setSelectedFilter] = useState('todo');
  const [selectedType, setSelectedType] = useState('todas');
  const [sortMode, setSortMode] = useState<SortMode>('recientes');
  const [expandedActivityId, setExpandedActivityId] = useState<string | null>(null);

  const filteredActivities = useMemo(() => {
    let activities = selectedFilter === 'todo' ? globalActivityEntries : getActivitiesByFilter(selectedFilter);
    if (selectedType !== 'todas') {
      activities = activities.filter((a) => a.type === selectedType);
    }
    const sorted = [...activities];
    if (sortMode === 'recientes') {
      sorted.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
    } else if (sortMode === 'mas_reacciones') {
      sorted.sort((a, b) => b.reactionsCount - a.reactionsCount);
    } else if (sortMode === 'mas_comentarios') {
      sorted.sort((a, b) => b.commentsCount - a.commentsCount);
    }
    return sorted;
  }, [selectedFilter, selectedType, sortMode]);

  const typeDistribution = useMemo(() => getActivityDistribution(), []);
  const filterDistribution = useMemo(() => getFilterDistribution(), []);
  const maxHourlyCount = Math.max(...mockGlobalActivityTimes.map((t) => t.count));

  return (
    <div className="min-h-screen bg-background-50">
      {/* Hero */}
      <div className="relative overflow-hidden bg-gradient-to-br from-teal-500 via-emerald-500 to-amber-500">
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/10 to-transparent"></div>
        <div className="relative max-w-6xl mx-auto px-4 md:px-6 py-12 md:py-16">
          {/* Breadcrumb */}
          <div className="flex items-center gap-2 text-xs text-white/70 mb-6">
            <a href="/" className="hover:text-white transition-colors cursor-pointer">RFW</a>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <span className="text-white font-medium">Actividad Global</span>
          </div>

          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
            <div>
              <h1 className="text-2xl md:text-4xl font-bold text-white mb-2">
                Actividad Global en Vivo
              </h1>
              <p className="text-sm text-white/80 max-w-lg">
                Cada comentario, cada nueva radio, cada ganador. Todo lo que está pasando en la comunidad RFW en tiempo real.
              </p>
            </div>
            <div className="flex items-center gap-4">
              {[
                { value: globalActivityStats.totalHoy, label: 'Hoy' },
                { value: globalActivityStats.ahoraActivos, label: 'Ahora' },
                { value: globalActivityStats.totalSemana, label: 'Semana' },
              ].map((m, i) => (
                <div key={i} className="text-center bg-white/15 backdrop-blur-sm rounded-lg px-4 py-2">
                  <div className="text-xl font-bold text-white">{m.value.toLocaleString()}</div>
                  <div className="text-[10px] text-white/70">{m.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Live Pulse Bar */}
      <div className="bg-background-100 border-b border-background-200/70">
        <div className="max-w-6xl mx-auto px-4 md:px-6 py-3">
          <div className="flex items-center gap-4 md:gap-6 overflow-x-auto">
            {[
              { label: 'Actividad Hoy', value: globalActivityStats.totalHoy.toLocaleString(), icon: 'ri-flashlight-line', color: 'text-teal-500' },
              { label: 'Racha Más Larga', value: '97', icon: 'ri-fire-line', color: 'text-orange-500' },
              { label: 'Completaron Hoy', value: '9,124', icon: 'ri-check-double-line', color: 'text-emerald-500' },
              { label: 'Subieron Nivel', value: '326', icon: 'ri-arrow-up-line', color: 'text-amber-500' },
              { label: 'En Riesgo', value: '847', icon: 'ri-alert-line', color: 'text-rose-500' },
              { label: 'Puntos Repartidos', value: '148K', icon: 'ri-coin-line', color: 'text-violet-500' },
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-1.5 whitespace-nowrap flex-shrink-0">
                <i className={`${item.icon} ${item.color} text-sm`}></i>
                <span className="text-xs text-foreground-500">{item.label}</span>
                <span className="text-xs font-bold text-foreground-950">{item.value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 md:px-6 py-8">
        <div className="flex flex-col lg:flex-row gap-6">
          {/* Sidebar */}
          <div className="lg:w-56 flex-shrink-0">
            <div className="bg-background-50 rounded-lg border border-background-200/70 p-4 sticky top-24">
              <div className="text-xs font-semibold text-foreground-400 uppercase tracking-wide mb-3">Vistas</div>
              <div className="space-y-1 mb-6">
                {([
                  { id: 'feed' as ViewMode, label: 'Feed Global', icon: 'ri-global-line', count: globalActivityEntries.length },
                  { id: 'categorias' as ViewMode, label: 'Categorías', icon: 'ri-price-tag-3-line', count: filterConfigs.length - 1 },
                  { id: 'estadisticas' as ViewMode, label: 'Estadísticas', icon: 'ri-bar-chart-2-line', count: null },
                  { id: 'reglas' as ViewMode, label: 'Reglas', icon: 'ri-scales-line', count: activityRules.length },
                ] as { id: ViewMode; label: string; icon: string; count: number | null }[]).map((item) => (
                  <button
                    key={item.id}
                    onClick={() => setView(item.id)}
                    className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-all cursor-pointer ${
                      view === item.id
                        ? 'bg-teal-50 text-teal-700 font-medium'
                        : 'text-foreground-600 hover:bg-background-100'
                    }`}
                  >
                    <span className="flex items-center gap-2">
                      <i className={`${item.icon} text-sm`}></i>
                      {item.label}
                    </span>
                    {item.count !== null && (
                      <span className={`text-xs px-1.5 py-0.5 rounded-full ${view === item.id ? 'bg-teal-100 text-teal-600' : 'bg-background-100 text-foreground-400'}`}>
                        {item.count}
                      </span>
                    )}
                  </button>
                ))}
              </div>

              {/* Quick nav: filter types */}
              <div className="text-xs font-semibold text-foreground-400 uppercase tracking-wide mb-3">Categorías</div>
              <div className="space-y-1">
                {filterConfigs.filter((f) => f.id !== 'todo').map((f) => {
                  const count = getActivitiesByFilter(f.id).length;
                  return (
                    <button
                      key={f.id}
                      onClick={() => { setView('feed'); setSelectedFilter(f.id); }}
                      className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-xs transition-all cursor-pointer ${
                        selectedFilter === f.id && view === 'feed'
                          ? 'bg-teal-50 text-teal-700 font-medium'
                          : 'text-foreground-500 hover:bg-background-100'
                      }`}
                    >
                      <span className="flex items-center gap-2">
                        <i className={`${f.icon} text-xs`}></i>
                        {f.label}
                      </span>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${selectedFilter === f.id && view === 'feed' ? 'bg-teal-100 text-teal-600' : 'bg-background-100 text-foreground-400'}`}>
                        {count}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Content Area */}
          <div className="flex-1 min-w-0">
            {/* View: Feed Global */}
            {view === 'feed' && (
              <div>
                {/* Filters Bar */}
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4">
                  <div className="flex flex-wrap items-center gap-1.5">
                    <span className="text-xs text-foreground-500 mr-1">Filtro:</span>
                    {filterConfigs.slice(0, 6).map((f) => {
                      const count = f.id === 'todo' ? globalActivityEntries.length : getActivitiesByFilter(f.id).length;
                      return (
                        <button
                          key={f.id}
                          onClick={() => setSelectedFilter(f.id)}
                          className={`px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap transition-all cursor-pointer flex items-center gap-1 ${
                            selectedFilter === f.id
                              ? 'bg-teal-500 text-white'
                              : 'bg-background-100 text-foreground-600 hover:bg-teal-100'
                          }`}
                        >
                          <i className={`${f.icon} text-xs`}></i>
                          {f.label}
                          <span className="text-[10px] opacity-70">{count}</span>
                        </button>
                      );
                    })}
                  </div>

                  {/* Type filter + sort */}
                  <div className="flex items-center gap-2">
                    <select
                      value={selectedType}
                      onChange={(e) => setSelectedType(e.target.value)}
                      className="text-xs bg-background-100 border border-background-200/70 rounded-full px-3 py-1.5 text-foreground-600 cursor-pointer outline-none"
                    >
                      <option value="todas">Todos los tipos</option>
                      {activityTypeConfigs.map((t) => (
                        <option key={t.id} value={t.id}>{t.label}</option>
                      ))}
                    </select>

                    <select
                      value={sortMode}
                      onChange={(e) => setSortMode(e.target.value as SortMode)}
                      className="text-xs bg-background-100 border border-background-200/70 rounded-full px-3 py-1.5 text-foreground-600 cursor-pointer outline-none"
                    >
                      <option value="recientes">Más Recientes</option>
                      <option value="mas_reacciones">Más Reacciones</option>
                      <option value="mas_comentarios">Más Comentarios</option>
                    </select>
                  </div>
                </div>

                {/* Activity Cards */}
                <div className="space-y-2">
                  {filteredActivities.map((activity) => {
                    const typeCfg = activityTypeConfigs.find((t) => t.id === activity.type);
                    const filterCfg = filterConfigs.find((f) => f.id === activity.filterId);
                    const isExpanded = expandedActivityId === activity.id;

                    return (
                      <div
                        key={activity.id}
                        className="bg-background-50 rounded-lg border border-background-200/70 hover:border-teal-200 transition-all"
                      >
                        <div className="p-4 flex gap-3">
                          {/* Avatar */}
                          <div className="flex-shrink-0">
                            <div className="w-11 h-11 rounded-full overflow-hidden border-2 border-background-200/70">
                              <img src={activity.user.avatar} alt={activity.user.name} className="w-full h-full object-cover" />
                            </div>
                          </div>

                          {/* Main */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center flex-wrap gap-2 mb-1.5">
                              <span className="font-semibold text-sm text-foreground-950">{activity.user.name}</span>
                              <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium flex items-center gap-1 bg-gradient-to-r ${typeCfg?.gradient ?? 'from-gray-400 to-gray-500'} text-white`}>
                                <i className={`${typeCfg?.icon ?? 'ri-circle-line'} text-[10px]`}></i>
                                {typeCfg?.label ?? activity.type}
                              </span>
                              {filterCfg && filterCfg.id !== 'todo' && (
                                <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-background-100 text-foreground-500 flex items-center gap-1">
                                  <i className={`${filterCfg.icon} text-[10px]`}></i>
                                  {filterCfg.label}
                                </span>
                              )}
                            </div>

                            <a
                              href={activity.target.link}
                              className="text-sm font-medium text-teal-600 hover:text-teal-700 cursor-pointer block mb-2"
                            >
                              {typeCfg?.verb ?? 'interactuó con'} {activity.target.name}
                              <span className="text-foreground-400 font-normal text-xs ml-1">· {activity.target.type}</span>
                            </a>

                            <p className={`text-sm text-foreground-600 leading-relaxed mb-3 ${isExpanded ? '' : 'line-clamp-2'}`}>
                              {activity.excerpt}
                            </p>

                            <div className="flex items-center gap-3 text-xs text-foreground-400">
                              <span className="flex items-center gap-1">
                                <i className="ri-time-line"></i>
                                {timeAgo(activity.timestamp)}
                              </span>
                              <button
                                onClick={() => {
                                  // Simulate reaction toggle
                                }}
                                className="flex items-center gap-1 hover:text-rose-500 transition-colors cursor-pointer"
                              >
                                <i className="ri-heart-line"></i>
                                <span>{activity.reactionsCount}</span>
                              </button>
                              <button
                                onClick={() => setExpandedActivityId(isExpanded ? null : activity.id)}
                                className="flex items-center gap-1 hover:text-sky-500 transition-colors cursor-pointer"
                              >
                                <i className="ri-chat-3-line"></i>
                                <span>{activity.commentsCount}</span>
                              </button>
                              <button className="flex items-center gap-1 hover:text-teal-500 transition-colors cursor-pointer">
                                <i className="ri-share-forward-line"></i>
                                <span className="hidden sm:inline">Compartir</span>
                              </button>
                            </div>

                            {/* Expanded comments preview */}
                            {isExpanded && (
                              <div className="mt-3 pt-3 border-t border-background-200/70">
                                <div className="text-xs font-medium text-foreground-600 mb-2">
                                  {activity.commentsCount} comentarios
                                </div>
                                <div className="space-y-2 mb-3">
                                  {[
                                    { user: 'Carlos M.', text: '¡Totalmente de acuerdo! Esto es lo que hace grande a RFW.', time: 'Hace 5 min' },
                                    { user: 'Luisa F.', text: 'Qué buena noticia. La comunidad no para de crecer.', time: 'Hace 12 min' },
                                    { user: 'Andrés R.', text: 'Increíble. Esto es lo que necesitábamos.', time: 'Hace 23 min' },
                                  ].map((c, ci) => (
                                    <div key={ci} className="flex gap-2">
                                      <div className="w-6 h-6 rounded-full bg-teal-100 flex items-center justify-center flex-shrink-0">
                                        <span className="text-[10px] font-bold text-teal-600">{c.user.charAt(0)}</span>
                                      </div>
                                      <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-1.5">
                                          <span className="text-xs font-semibold text-foreground-800">{c.user}</span>
                                          <span className="text-[10px] text-foreground-400">{c.time}</span>
                                        </div>
                                        <p className="text-xs text-foreground-600">{c.text}</p>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                                <div className="flex gap-2">
                                  <input
                                    type="text"
                                    placeholder="Escribe un comentario..."
                                    className="flex-1 text-xs bg-background-100 border border-background-200/70 rounded-full px-3 py-1.5 text-foreground-600 outline-none focus:border-teal-300 transition-colors"
                                  />
                                  <button className="px-3 py-1.5 bg-teal-500 text-white rounded-full text-xs font-medium hover:bg-teal-600 whitespace-nowrap cursor-pointer transition-colors">
                                    Enviar
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {filteredActivities.length === 0 && (
                  <div className="text-center py-16">
                    <div className="w-20 h-20 rounded-full bg-background-200/50 flex items-center justify-center mx-auto mb-4">
                      <i className="ri-inbox-line text-3xl text-foreground-400"></i>
                    </div>
                    <p className="text-sm text-foreground-500 mb-1">No hay actividad en este momento.</p>
                    <p className="text-xs text-foreground-400">Probá cambiando los filtros o volvé más tarde.</p>
                  </div>
                )}
              </div>
            )}

            {/* View: Categories */}
            {view === 'categorias' && (
              <div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {filterConfigs.filter((f) => f.id !== 'todo').map((f) => {
                    const activities = getActivitiesByFilter(f.id);
                    const topActivities = activities.slice(0, 3);
                    return (
                      <div key={f.id} className="bg-background-50 rounded-lg border border-background-200/70 p-5 hover:border-teal-200 transition-colors">
                        <div className="flex items-center gap-3 mb-4">
                          <div className={`w-12 h-12 rounded-xl bg-${f.color}-50 flex items-center justify-center`}>
                            <i className={`${f.icon} text-xl text-${f.color}-500`}></i>
                          </div>
                          <div>
                            <div className="font-semibold text-base text-foreground-950">{f.label}</div>
                            <div className="text-xs text-foreground-500">{activities.length} actividades recientes</div>
                          </div>
                        </div>
                        <p className="text-xs text-foreground-600 leading-relaxed mb-4">
                          {f.id === 'radios' && 'Nuevas emisoras, subidas de contenido y concursos de todas las radios en RFW.'}
                          {f.id === 'programas' && 'Programas nuevos, episodios, listas y contenido de los shows más escuchados.'}
                          {f.id === 'locutores' && 'Entrevistas, videos y actividad de los locutores más queridos de la comunidad.'}
                          {f.id === 'clubes' && 'Nuevos clubes, eventos exclusivos y la vida social de la comunidad RFW.'}
                          {f.id === 'eventos' && 'Conciertos, transmisiones especiales, talleres y todo lo que está por venir.'}
                        </p>
                        <div className="space-y-2">
                          {topActivities.map((a, idx) => (
                            <div key={idx} className="flex items-center gap-2 text-xs">
                              <div className="w-5 h-5 rounded-full overflow-hidden flex-shrink-0">
                                <img src={a.user.avatar} alt="" className="w-full h-full object-cover" />
                              </div>
                              <span className="text-foreground-700 truncate">
                                <strong>{a.user.name}</strong> {a.type === 'comentario' ? 'comentó' : a.type === 'subida' ? 'subió' : a.type === 'nuevo_concurso' ? 'lanzó concurso' : a.type === 'nuevo_ganador' ? 'ganó' : a.type === 'nuevo_evento' ? 'creó evento' : a.type === 'nueva_radio' ? 'se unió' : 'fundó'}
                              </span>
                              <span className="text-foreground-400 ml-auto whitespace-nowrap">{timeAgo(a.timestamp)}</span>
                            </div>
                          ))}
                        </div>
                        <button
                          onClick={() => { setView('feed'); setSelectedFilter(f.id); }}
                          className="mt-4 w-full text-center text-xs font-medium text-teal-600 hover:text-teal-700 cursor-pointer py-1.5 bg-teal-50 rounded-full transition-colors"
                        >
                          Ver todo en {f.label.toLowerCase()}
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* View: Statistics */}
            {view === 'estadisticas' && (
              <div>
                {/* Activity Now */}
                <div className="bg-background-50 rounded-lg border border-background-200/70 p-5 mb-4">
                  <div className="text-sm font-semibold text-foreground-950 mb-3">Actividad en Vivo Ahora</div>
                  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
                    {[
                      { label: 'Comentando', value: globalActivityStats.ahoraComentando, icon: 'ri-chat-3-line', bg: 'bg-sky-50', color: 'text-sky-500' },
                      { label: 'Subiendo', value: globalActivityStats.ahoraSubiendo, icon: 'ri-upload-cloud-2-line', bg: 'bg-emerald-50', color: 'text-emerald-500' },
                      { label: 'Compitiendo', value: globalActivityStats.ahoraCompitiendo, icon: 'ri-trophy-line', bg: 'bg-amber-50', color: 'text-amber-500' },
                      { label: 'Ganando', value: globalActivityStats.ahoraGanadores, icon: 'ri-medal-line', bg: 'bg-rose-50', color: 'text-rose-500' },
                      { label: 'Eventos Activos', value: globalActivityStats.ahoraEventos, icon: 'ri-calendar-event-line', bg: 'bg-violet-50', color: 'text-violet-500' },
                      { label: 'Radios Nuevas', value: globalActivityStats.ahoraNuevasRadios, icon: 'ri-radio-line', bg: 'bg-orange-50', color: 'text-orange-500' },
                      { label: 'Clubes Nuevos', value: globalActivityStats.ahoraNuevosClubes, icon: 'ri-group-line', bg: 'bg-fuchsia-50', color: 'text-fuchsia-500' },
                    ].map((item, i) => (
                      <div key={i} className={`${item.bg} rounded-lg p-3 text-center`}>
                        <i className={`${item.icon} ${item.color} text-xl block mb-1`}></i>
                        <div className={`text-xl font-bold ${item.color}`}>{item.value}</div>
                        <div className="text-[10px] text-foreground-500">{item.label}</div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Hourly Activity */}
                <div className="bg-background-50 rounded-lg border border-background-200/70 p-5 mb-4">
                  <div className="text-sm font-semibold text-foreground-950 mb-1">Actividad por Hora</div>
                  <div className="text-xs text-foreground-400 mb-4">Pico: {globalActivityStats.picoHorario} · Promedio: {globalActivityStats.promedioPorHora}/h</div>
                  <div className="flex items-end gap-1 h-32">
                    {mockGlobalActivityTimes.map((h) => (
                      <div key={h.time} className="flex-1 flex flex-col items-center justify-end h-full">
                        <div
                          className="w-full rounded-t-sm bg-gradient-to-t from-teal-500 to-emerald-400 transition-all"
                          style={{ height: `${(h.count / maxHourlyCount) * 100}%`, minHeight: h.count > 0 ? '4px' : '0px' }}
                        ></div>
                        <span className="text-[9px] text-foreground-400 mt-1">{h.time}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Distribution */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* By Type */}
                  <div className="bg-background-50 rounded-lg border border-background-200/70 p-5">
                    <div className="text-sm font-semibold text-foreground-950 mb-3">Por Tipo de Actividad</div>
                    <div className="space-y-3">
                      {typeDistribution.map((item) => {
                        const maxCount = Math.max(...typeDistribution.map((d) => d.count));
                        const barWidth = (item.count / maxCount) * 100;
                        return (
                          <div key={item.type} className="flex items-center gap-3">
                            <div className="w-28 flex items-center gap-1.5 flex-shrink-0">
                              <i className={`${item.config?.icon ?? 'ri-circle-line'} text-sm`} style={{ color: `var(--${item.config?.color ?? 'foreground'}-500)` }}></i>
                              <span className="text-xs text-foreground-600">{item.config?.label ?? item.type}</span>
                            </div>
                            <div className="flex-1 h-5 bg-background-100 rounded-full overflow-hidden">
                              <div
                                className={`h-full rounded-full bg-gradient-to-r ${item.config?.gradient ?? 'from-gray-400 to-gray-500'}`}
                                style={{ width: `${barWidth}%` }}
                              ></div>
                            </div>
                            <span className="text-xs font-semibold text-foreground-700 w-8 text-right">{item.count}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* By Category */}
                  <div className="bg-background-50 rounded-lg border border-background-200/70 p-5">
                    <div className="text-sm font-semibold text-foreground-950 mb-3">Por Categoría</div>
                    <div className="space-y-3">
                      {filterDistribution.filter((f) => f.config && f.filterId !== 'todo').map((item) => {
                        const maxCount = Math.max(...filterDistribution.filter((f) => f.filterId !== 'todo').map((d) => d.count));
                        const barWidth = (item.count / maxCount) * 100;
                        return (
                          <div key={item.filterId} className="flex items-center gap-3">
                            <div className="w-24 flex items-center gap-1.5 flex-shrink-0">
                              <i className={`${item.config?.icon ?? 'ri-global-line'} text-sm`} style={{ color: `var(--${item.config?.color ?? 'foreground'}-500)` }}></i>
                              <span className="text-xs text-foreground-600">{item.config?.label ?? item.filterId}</span>
                            </div>
                            <div className="flex-1 h-5 bg-background-100 rounded-full overflow-hidden">
                              <div
                                className="h-full rounded-full"
                                style={{
                                  width: `${barWidth}%`,
                                  background: `linear-gradient(to right, var(--${item.config?.color ?? 'foreground'}-400), var(--${item.config?.color ?? 'foreground'}-500))`,
                                }}
                              ></div>
                            </div>
                            <span className="text-xs font-semibold text-foreground-700 w-8 text-right">{item.count}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>

                {/* Entity Totals */}
                <div className="bg-background-50 rounded-lg border border-background-200/70 p-5 mt-4">
                  <div className="text-sm font-semibold text-foreground-950 mb-3">Entidades en RFW</div>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                    <div className="bg-orange-50 rounded-lg p-3 text-center">
                      <i className="ri-radio-line text-lg text-orange-500 block mb-1"></i>
                      <div className="text-xl font-bold text-foreground-950">{globalActivityStats.totalEntidades.radios.toLocaleString()}</div>
                      <div className="text-xs text-foreground-400">Radios</div>
                    </div>
                    <div className="bg-violet-50 rounded-lg p-3 text-center">
                      <i className="ri-calendar-check-line text-lg text-violet-500 block mb-1"></i>
                      <div className="text-xl font-bold text-foreground-950">{globalActivityStats.totalEntidades.programas.toLocaleString()}</div>
                      <div className="text-xs text-foreground-400">Programas</div>
                    </div>
                    <div className="bg-sky-50 rounded-lg p-3 text-center">
                      <i className="ri-mic-line text-lg text-sky-500 block mb-1"></i>
                      <div className="text-xl font-bold text-foreground-950">{globalActivityStats.totalEntidades.locutores.toLocaleString()}</div>
                      <div className="text-xs text-foreground-400">Locutores</div>
                    </div>
                    <div className="bg-fuchsia-50 rounded-lg p-3 text-center">
                      <i className="ri-group-line text-lg text-fuchsia-500 block mb-1"></i>
                      <div className="text-xl font-bold text-foreground-950">{globalActivityStats.totalEntidades.clubes.toLocaleString()}</div>
                      <div className="text-xs text-foreground-400">Clubes</div>
                    </div>
                    <div className="bg-amber-50 rounded-lg p-3 text-center">
                      <i className="ri-calendar-event-line text-lg text-amber-500 block mb-1"></i>
                      <div className="text-xl font-bold text-foreground-950">{globalActivityStats.totalEntidades.eventos.toLocaleString()}</div>
                      <div className="text-xs text-foreground-400">Eventos</div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* View: Rules */}
            {view === 'reglas' && (
              <div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                  {activityRules.map((rule) => (
                    <div key={rule.id} className="bg-background-50 rounded-lg p-5 border border-background-200/70 hover:border-teal-200 transition-colors">
                      <div className={`w-12 h-12 rounded-xl bg-${rule.color}-50 flex items-center justify-center mb-4`}>
                        <i className={`${rule.icon} text-xl text-${rule.color}-500`}></i>
                      </div>
                      <div className="font-semibold text-sm text-foreground-950 mb-3">{rule.title}</div>
                      <div className="text-xs text-foreground-600 leading-relaxed">{rule.description}</div>
                    </div>
                  ))}
                </div>

                {/* Real-time example */}
                <div className="bg-teal-50 rounded-lg border border-teal-200 p-5 mb-4">
                  <div className="flex items-center gap-2 mb-3">
                    <i className="ri-eye-line text-teal-600"></i>
                    <span className="font-semibold text-sm text-teal-700">Así funciona en tiempo real</span>
                  </div>
                  <div className="text-sm text-teal-700 leading-relaxed">
                    Cada 30 segundos, el sistema escanea toda la actividad de la plataforma. Detecta nuevos comentarios, subidas, concursos, ganadores, eventos, nuevas radios y nuevos clubes. Cada acción genera una entrada en el feed global automáticamente. No hay curación manual: lo que ves es exactamente lo que está pasando. El feed solo muestra actividad de las últimas 48 horas, garantizando que siempre sea contenido fresco y relevante.
                  </div>
                </div>

                {/* Distribution summary */}
                <div className="bg-background-50 rounded-lg border border-background-200/70 p-5">
                  <div className="text-sm font-semibold text-foreground-950 mb-3">Resumen de Distribución</div>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {filterDistribution.filter((f) => f.config && f.filterId !== 'todo').map((f) => (
                      <div key={f.filterId} className="bg-background-100 rounded-lg p-3 text-center">
                        <i className={`${f.config?.icon ?? 'ri-global-line'} text-lg mb-1 block`} style={{ color: `var(--${f.config?.color ?? 'foreground'}-500)` }}></i>
                        <div className="text-lg font-bold text-foreground-950">{f.count}</div>
                        <div className="text-xs text-foreground-400">{f.config?.label ?? f.filterId}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Cross-Links */}
            <div className="mt-8 pt-6 border-t border-background-200/70">
              <div className="text-xs font-semibold text-foreground-400 uppercase tracking-wide mb-3">Explorar más</div>
              <div className="flex flex-wrap gap-2">
                {[
                  { label: 'Gamificación', href: '/gamificacion', icon: 'ri-gamepad-line' },
                  { label: 'Rankings', href: '/rankings', icon: 'ri-trophy-line' },
                  { label: 'Rachas', href: '/rachas', icon: 'ri-fire-line' },
                  { label: 'Aniversarios', href: '/aniversarios', icon: 'ri-cake-line' },
                  { label: 'Centro Social', href: '/centro-social', icon: 'ri-heart-pulse-line' },
                  { label: 'Clubes', href: '/clubes', icon: 'ri-group-line' },
                  { label: 'Eventos', href: '/eventos', icon: 'ri-calendar-event-line' },
                  { label: 'Explorar', href: '/explorar', icon: 'ri-compass-3-line' },
                ].map((link) => (
                  <a
                    key={link.href}
                    href={link.href}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-background-100 hover:bg-teal-100 rounded-full text-xs text-foreground-600 hover:text-teal-700 transition-colors cursor-pointer"
                  >
                    <i className={`${link.icon} text-xs`}></i>
                    {link.label}
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Final Rule Banner */}
      <div className="bg-teal-500 py-4 mt-8">
        <div className="max-w-6xl mx-auto px-4 md:px-6 text-center">
          <p className="text-sm text-white font-medium">
            <i className="ri-eye-line mr-1.5"></i>
            Siempre mostrar actividad reciente real.
          </p>
        </div>
      </div>
    </div>
  );
}